import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { formatErr } from "@/lib/api";

export default function ChangeLoginMobile({ open, onClose }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [password, setPassword] = useState("");
  const [mobile, setMobile] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChallenge(null); setOtp(""); setPassword(""); setMobile("");
    api.get("/auth/mobile").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, [open]);

  const close = () => { if (!busy) onClose(); };

  const requestOtp = async (event) => {
    event.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/auth/mobile/request-otp", { current_password: password, new_mobile: mobile });
      setChallenge(data); setPassword("");
      toast.success(data.development_otp ? "Local test code is shown below" : `Security code sent to ${data.masked_email}`);
    } catch (error) { toast.error(formatErr(error, "Could not verify the mobile change")); }
    finally { setBusy(false); }
  };

  const verifyOtp = async (event) => {
    event.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/mobile/verify-otp", { challenge_id: challenge.challenge_id, otp });
      toast.success("Login mobile updated. Please log in again.");
      logout(); navigate("/login", { replace: true });
    } catch (error) { toast.error(formatErr(error, "Incorrect or expired security code")); }
    finally { setBusy(false); }
  };

  const warning = status?.mobile_login_status === "duplicate_requires_correction"
    ? "This account shares its current number with another user. Mobile login is disabled until you save a unique number."
    : status?.mobile_login_status === "invalid_requires_correction"
      ? "The current number is invalid. Save a valid mobile number to enable mobile login."
      : null;

  return <Dialog open={open} onOpenChange={(next) => !next && close()}>
    <DialogContent className="max-w-md rounded-2xl" data-testid="change-login-mobile-modal">
      <DialogHeader><DialogTitle className="font-heading text-xl font-bold">My Account · Login mobile</DialogTitle></DialogHeader>
      {!challenge ? <form onSubmit={requestOtp} className="space-y-4 mt-2">
        <div className="rounded-xl bg-slate-50 p-3 text-sm">
          <p className="text-slate-500">Current login mobile</p>
          <p className="font-bold mt-1">{status?.masked_phone || "Not configured"}</p>
          {status?.mobile_login_enabled && <p className="text-xs text-emerald-700 mt-1">Mobile login enabled</p>}
          {warning && <p className="text-xs text-amber-700 mt-2">{warning}</p>}
        </div>
        <div><Label htmlFor="login-mobile-new">New login mobile number</Label><Input id="login-mobile-new" data-testid="login-mobile-new" type="tel" inputMode="tel" required autoComplete="tel" className="mt-1.5" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="7019264413 or +917019264413" /><p className="text-xs text-slate-500 mt-1">This belongs to your user account, not the School/Centre Profile.</p></div>
        <div><Label htmlFor="login-mobile-password">Current password</Label><Input id="login-mobile-password" data-testid="login-mobile-password" type="password" required autoComplete="current-password" className="mt-1.5" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
        <Button disabled={busy} className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white">{busy ? "Verifying…" : "Verify Password & Send Code"}</Button>
      </form> : <form onSubmit={verifyOtp} className="space-y-4 mt-2">
        <p className="text-sm text-slate-500">Enter the six-digit code sent to <b>{challenge.masked_email}</b>. It expires in five minutes.</p>
        {challenge.development_otp && <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-center"><p className="text-xs font-bold text-amber-700">LOCAL TEST OTP — NEVER SHOWN IN PRODUCTION</p><p className="font-mono text-2xl font-extrabold tracking-[0.35em] mt-1">{challenge.development_otp}</p></div>}
        <div><Label htmlFor="login-mobile-otp">Email security code</Label><Input id="login-mobile-otp" data-testid="login-mobile-otp" autoFocus required inputMode="numeric" pattern="[0-9]{6}" maxLength={6} className="mt-1.5 text-center text-xl tracking-[0.4em]" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} /></div>
        <Button disabled={busy || otp.length !== 6} className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white">{busy ? "Updating…" : "Verify & Change Login Mobile"}</Button>
        <Button type="button" variant="ghost" className="w-full" onClick={() => { setChallenge(null); setOtp(""); }}>Start again</Button>
      </form>}
    </DialogContent>
  </Dialog>;
}
